import {AxiosError} from 'axios';
import {useRouter} from 'next/router';
import {useInfiniteQuery, UseInfiniteQueryOptions, useQueryClient} from '@tanstack/react-query';

import {api} from '@eksab/api';
import {useAccessToken} from '@eksab/hooks/useAccessToken';
import type {TriviaGame} from '@eksab/features/play/types';

import {ChallengeFilters, useFiltersState} from '../components/Filters';
import type {Challenge} from '../types';

type PaginatedChallenges = {
  data: Challenge[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
  };
};

const queryKey = (
  game: TriviaGame,
  locale: Locale,
  filters: Partial<ChallengeFilters> = useFiltersState.emptyState,
  isLoggedIn = false,
) => ['trivia-challenges', game, filters, locale, isLoggedIn];

const queryFn = (game: TriviaGame, filters?: ChallengeFilters, page = 1, isLoggedIn = false) =>
  api
    .get<PaginatedChallenges>(`v1/challenges${isLoggedIn ? '/auth' : ''}`, {
      params: {
        filters: {...filters, game},
        page: {
          page,
          per_page: 9,
        },
      },
    })
    .then((res) => res.data);

export const useChallengesQuery = (
  game: TriviaGame,
  filters: ChallengeFilters,
  options?: UseInfiniteQueryOptions<PaginatedChallenges, AxiosError>,
) => {
  const locale = useRouter().locale;
  const isLoggedIn = useAccessToken().isSuccess;

  return useInfiniteQuery<PaginatedChallenges, AxiosError>(
    queryKey(game, locale, filters, isLoggedIn),
    ({pageParam}) => queryFn(game, filters, pageParam, isLoggedIn),
    {
      ...options,
      getNextPageParam: ({meta}) =>
        meta.current_page < meta.total / meta.per_page ? meta.current_page + 1 : undefined,
    },
  );
};
export const useInvalidateChallenges = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries<PaginatedChallenges>(['trivia-challenges']);
};

useChallengesQuery.queryKey = queryKey;
useChallengesQuery.queryFn = queryFn;
